import React, { useMemo } from 'react';
import { Link } from 'react-router';
import { ArrowRight, CheckCircle2, ListChecks } from 'lucide-react';
import { useLeagueReadiness } from '../hooks/useLeagueReadiness';
import { mapReadinessToSetupSummary } from '../leagueReadinessMapper';
import { lscPath } from '../routing';
import ErrorBanner from './ErrorBanner';
import LoadingState from './LoadingState';

interface LeagueSetupHomeCardProps {
  leagueId: string;
  isOwner: boolean;
}

const LeagueSetupHomeCard: React.FC<LeagueSetupHomeCardProps> = ({ leagueId, isOwner }) => {
  const { readiness, loading, error, authError, reload } = useLeagueReadiness(leagueId);

  const summary = useMemo(
    () => (readiness ? mapReadinessToSetupSummary(readiness) : null),
    [readiness]
  );

  const setupHref = lscPath(`/leagues/${leagueId}/setup`);
  const soleBlocker = summary && summary.topBlockers.length === 1 ? summary.topBlockers[0] : null;
  const descriptionText = (summary?.description ?? '').trim();
  const descriptionRepeatsHeadline = descriptionText === (summary?.headline ?? '').trim();
  const descriptionRepeatsSoleBlocker =
    soleBlocker != null && descriptionText === soleBlocker.description.trim();
  const showHeaderDescription = !descriptionRepeatsHeadline && !descriptionRepeatsSoleBlocker;
  const needsAttentionHref =
    summary?.operationalAttentionHref ?? lscPath(`/leagues/${leagueId}/needs-attention`);

  if (loading) {
    return (
      <section className="card" data-testid="league-setup-home-card">
        <LoadingState message="Loading setup status…" />
      </section>
    );
  }

  if (authError === 'unauthorized') {
    return (
      <section className="card" data-testid="league-setup-home-card">
        <ErrorBanner message="Sign in to view league setup status." />
      </section>
    );
  }

  if (authError === 'forbidden') {
    return (
      <section className="card" data-testid="league-setup-home-card">
        <ErrorBanner message="You do not have permission to view league setup status." />
      </section>
    );
  }

  if (error || !summary) {
    return (
      <section className="card space-y-3" data-testid="league-setup-home-card">
        <ErrorBanner message={error ?? 'Unable to load setup status. Try again in a moment.'} />
        <button type="button" className="btn-secondary text-sm" onClick={reload}>
          Retry
        </button>
      </section>
    );
  }

  if (summary.showCompactReady) {
    return (
      <section
        className="card flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
        data-testid="league-setup-home-ready"
      >
        <div className="flex items-start gap-3 min-w-0">
          <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" aria-hidden />
          <div className="space-y-1 min-w-0">
            <h2 className="text-lg font-semibold text-white">League ready</h2>
            <p className="text-sm text-secondary-400">{summary.description}</p>
          </div>
        </div>
        <div className="flex flex-col sm:items-end gap-2 shrink-0">
          <Link
            to={setupHref}
            className="text-sm font-medium text-primary-300 hover:text-primary-200"
          >
            Review setup
          </Link>
          {summary.operationalAttentionCount > 0 ? (
            <Link
              to={needsAttentionHref}
              className="text-sm font-medium text-amber-200 hover:text-amber-100"
              data-testid="league-setup-home-needs-attention"
            >
              View Needs Attention
            </Link>
          ) : null}
        </div>
      </section>
    );
  }

  return (
    <section className="card space-y-4" data-testid="league-setup-home-blockers">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <ListChecks className="h-5 w-5 text-primary-400 shrink-0 mt-0.5" aria-hidden />
          <div className="space-y-1 min-w-0">
            <h2 className="text-lg font-semibold text-white">{summary.headline}</h2>
            {showHeaderDescription ? (
              <p className="text-sm text-secondary-400">{summary.description}</p>
            ) : null}
            <p className="text-xs text-secondary-500">
              {summary.blockerCount} blocker{summary.blockerCount === 1 ? '' : 's'}
              {summary.warningCount > 0
                ? ` · ${summary.warningCount} warning${summary.warningCount === 1 ? '' : 's'}`
                : null}
            </p>
          </div>
        </div>
        <Link
          to={setupHref}
          className="btn-primary inline-flex items-center gap-2 shrink-0 self-start"
          data-testid="league-setup-home-open-wizard"
        >
          Open setup wizard
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>

      {summary.topBlockers.length > 0 ? (
        <ul className="space-y-2 text-sm text-secondary-300">
          {summary.topBlockers.map((blocker) => (
            <li key={blocker.key} className="rounded-lg border border-secondary-700/80 px-3 py-2">
              <p className="font-medium text-white">{blocker.title}</p>
              <p className="text-secondary-400 mt-0.5">{blocker.description}</p>
            </li>
          ))}
        </ul>
      ) : null}

      {!isOwner ? (
        <p className="text-xs text-secondary-500">
          Some setup steps may require the league owner.
        </p>
      ) : null}
    </section>
  );
};

export default LeagueSetupHomeCard;
